import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Navigate } from 'react-router-dom';
import routes from './routes';
import AuthService from '../services/AuthService';
import PortalAuthService from '../services/PortalAuthService';
import { selectStoredPermissions } from '../stores/permissionsSlice';
import { BASE_PATH, CURRENT_SITE } from '../services/apiConfig';
import ApiService from '../services/ApiService';

// Layouts are lazy so each site only loads its own stylesheet
const Layout = lazy(() => import('./Layout'));
const WebsiteLayout = lazy(() => import('./WebsiteLayout'));


// Mounts the routes of the site this URL belongs to: <root>/ website, <root>/admin, <root>/facility.
const AppRouter = () => {
    const isWebsite = CURRENT_SITE === 'website';
    const isPortal = CURRENT_SITE === 'facility';
    const loggedIn = isPortal ? PortalAuthService.getCurrentUser() : AuthService.getCurrentUser();
    const siteRoutes = routes.filter(route => route.meta.site === CURRENT_SITE);

    // Permissions are saved at login; refresh them on load so newly added modules
    // (or role changes) show up without logging out and in again.
    const [, setPermissionsVersion] = useState(0);
    useEffect(() => {
        if (CURRENT_SITE !== 'admin' || !loggedIn) return;
        ApiService.request({ method: 'GET', url: 'my-permissions', silent: true })
            .then(({ data }) => {
                if (!data.status) return;
                const fresh = data.data.permissions || [];
                const current = selectStoredPermissions();
                const changed = fresh.length !== current.length || fresh.some(p => !current.includes(p));
                if (changed) {
                    localStorage.setItem('permissions', JSON.stringify(fresh));
                    setPermissionsVersion(v => v + 1);
                }
            })
            .catch(() => {});
    }, []);

    return (
        <Router basename={BASE_PATH}>
            <Suspense fallback={null}>
            <Routes>
                {!isWebsite && <Route path="/" element={<Navigate to="/dashboard" />} />}
                {/* Unknown website / portal URLs go to their home page (the admin side has its own 404 route) */}
                {isWebsite && <Route path="*" element={<Navigate to="/" replace />} />}
                {isPortal && <Route path="*" element={<Navigate to="/dashboard" />} />}
                {siteRoutes.map((route, index) => {
                    const Component = route.component;

                    if (isWebsite) {
                        return (
                            <Route key={index} path={route.path} element={<WebsiteLayout><Component /></WebsiteLayout>} />
                        );
                    }

                    const requiresAuth = route.meta.requiresAuth;

                    let hasPermission = true;
                    if (!isPortal && route.meta.permission) {
                        hasPermission = selectStoredPermissions().includes(route.meta.permission);
                    }
                    const hideHeader = route.meta.hideHeader;

                    return (
                        <Route
                            key={index}
                            path={route.path}
                            element={
                                requiresAuth && !loggedIn ? (
                                    <Navigate to="/login" />
                                ): (
                                    <Layout hideHeader={hideHeader} hasPermission={hasPermission} portal={isPortal} >
                                        <Component  />
                                    </Layout>
                                )
                            }
                        />
                    );
                })}
            </Routes>
            </Suspense>
        </Router>
    );
};

export default AppRouter;
